import type { FormEvent } from 'react';
import type { ClientRegistrationFields } from 'types/registration';
import { i18n as I18n, useTranslation } from 'utils/i18n';
import * as yup from 'yup';
// Constants
import { companyTypes } from '../../../constants/fieldData';
import { POSTCODE_REGEX } from '../../../constants/registration';
// Hooks
import useValidation from '../../../hooks/useValidation';
import type { FieldChangeEvent } from '../../../types';
import { formatTextAsParagraphs } from '../../../utils/display';
import { validateXSS, xssErrorMessage } from '../../../validators';
import {
  AddressLineOneField,
  AddressLineTwoField,
  CountyField,
  PostCodeField,
  TownField,
} from '../../fields/clientRegistration/companyDetails';
// Components
import ClientSelect from '../../fields/clientSelect';
import SelectList from '../../fields/selectList';
import InstructionalFormActions from '../../instructionalForm/actions';

const getNewCompanySchema = () =>
  yup.object().shape({
    companyName: yup
      .object()
      .shape({
        label: yup
          .string()
          .test('no-xss', xssErrorMessage, value => validateXSS(value!)),
        value: yup.number(),
      })
      .nullable()
      .required(
        I18n.t('validations.required', {
          name: I18n.t(
            'registration.company_details.fields.company_name.label'
          ),
        })
      ),
    address1: yup
      .string()
      .test('no-xss', xssErrorMessage, value => validateXSS(value!))
      .required(
        I18n.t('validations.required', {
          name: I18n.t('registration.company_details.fields.address1.label'),
        })
      ),
    address2: yup
      .string()
      .test('no-xss', xssErrorMessage, value => validateXSS(value!)),
    town: yup
      .string()
      .test('no-xss', xssErrorMessage, value => validateXSS(value!))
      .required(
        I18n.t('validations.required', {
          name: I18n.t('registration.company_details.fields.town.label'),
        })
      ),
    county: yup
      .string()
      .test('no-xss', xssErrorMessage, value => validateXSS(value!))
      .required(
        I18n.t('validations.required', {
          name: I18n.t('registration.company_details.fields.county.label'),
        })
      ),
    postcode: yup
      .string()
      .test('no-xss', xssErrorMessage, value => validateXSS(value!))

      .required(
        I18n.t('validations.required', {
          name: I18n.t('registration.company_details.fields.postcode.label'),
        })
      )
      .matches(POSTCODE_REGEX, I18n.t('validations.postcode')),
    companyType: yup
      .string()
      .test('no-xss', xssErrorMessage, value => validateXSS(value!))
      .required(
        I18n.t('validations.required', {
          name: I18n.t(
            'registration.company_details.fields.company_type.label'
          ),
        })
      ),
  });

const getExistingCompanySchema = () =>
  yup.object().shape({
    companyName: yup
      .object()
      .shape({
        label: yup
          .string()
          .test('no-xss', xssErrorMessage, value => validateXSS(value!)),
        value: yup.number(),
      })
      .nullable()
      .required(
        I18n.t('validations.required', {
          name: I18n.t(
            'registration.company_details.fields.company_name.label'
          ),
        })
      ),
  });

interface CompanyDetailsProps {
  fields: ClientRegistrationFields;
  existingCompany?: boolean;
  handleFieldChange: (event: FieldChangeEvent<unknown>) => void;
  handleSubmitCallback: (fields: ClientRegistrationFields) => void;
  handleGoBack?: () => void;
  backButtonEnabled?: boolean;
}

const CompanyDetails = ({
  fields,
  existingCompany,
  handleFieldChange,
  handleSubmitCallback,
  handleGoBack,
  backButtonEnabled = true,
}: CompanyDetailsProps) => {
  const schema = existingCompany
    ? getExistingCompanySchema()
    : getNewCompanySchema();
  const {
    errors,
    validate,
    handleChange: handleFieldValidation,
    isValid,
  } = useValidation(fields, schema);
  const { t } = useTranslation('common');

  const handleChange = (event: FieldChangeEvent<unknown>) => {
    handleFieldChange(event);
    handleFieldValidation(event);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    validate();

    if (isValid()) {
      handleSubmitCallback(fields);
    }

    return false;
  };

  return (
    <form
      className="instructional-form__form"
      onSubmit={handleSubmit}
      data-testid="companyDetailsForm"
      data-cy="companyDetailsForm"
    >
      <h2 data-cy="registrationHeader">
        {t('registration.company_details.title')}
      </h2>

      <ClientSelect
        label={t('registration.company_details.fields.company_name.label')}
        placeholder={t(
          'registration.company_details.fields.company_name.label'
        )}
        name="companyName"
        value={fields.companyName}
        error={!!errors.companyName}
        errorMsg={errors.companyName}
        handleChange={handleChange}
      />

      {existingCompany &&
        formatTextAsParagraphs(
          t('registration.company_details.existing_company_instructions')
        )}

      {!existingCompany && (
        <>
          {/* NHS-2155 Comment out company address lookup until QAS integration
            <Textbox
              label={t(
                'registration.company_details.fields.company_lookup.label'
              )}
              name="lookup"
              value={fields.lookup}
              placeholder={t(
                'registration.company_details.fields.company_lookup.placeholder'
              )}
            />
          */}

          <div className="field-multicol">
            <AddressLineOneField
              value={fields.address1}
              error={errors.address1}
              onChange={handleChange}
            />

            <AddressLineTwoField
              value={fields.address2}
              error={errors.address2}
              onChange={handleChange}
            />
          </div>
          <div className="field-multicol">
            <TownField
              value={fields.town}
              error={errors.town}
              onChange={handleChange}
            />
            <CountyField
              value={fields.county}
              error={errors.county}
              onChange={handleChange}
            />
          </div>
          <PostCodeField
            value={fields.postcode}
            error={errors.postcode}
            onChange={handleChange}
          />
          <SelectList
            label={t('registration.company_details.fields.company_type.label')}
            name="companyType"
            placeholder={t(
              'registration.company_details.fields.company_type.label'
            )}
            options={companyTypes}
            value={fields.companyType}
            error={!!errors.companyType}
            errorMsg={errors.companyType}
            handleChangeCallback={handleChange}
          />
        </>
      )}

      <InstructionalFormActions
        isValid={isValid()}
        showBackBtn={backButtonEnabled}
        backBtnText={t('back_to_step', { step: 1 })}
        handleBackBtnClick={handleGoBack}
      />
    </form>
  );
};

export default CompanyDetails;
